/**
 * Assessable assets & income — what a client's balance sheet and income look like to the MEANS TESTS,
 * as a pure function.
 *
 * This is the walk that decides WHAT COUNTS, before any test is applied to it: which assets are
 * assessable and which are exempt (the family home while someone lives in it, a RAD, funeral bonds up
 * to the per-person limit), which of them are FINANCIAL assets and so get deemed, and which income
 * streams are assessable and whose they are (client, partner, or split 50/50). It also strikes the one
 * genuinely intricate piece of income assessment in the fact-find: a pre-1 January 2015 account-based
 * pension, whose assessable income is its annual payment less a deductible amount derived from the
 * purchase price, the commutations taken out of it and the owner's life expectancy at commencement.
 *
 * What it deliberately does NOT do is test any of it. The totals here are struck once and read TWICE —
 * by the Age Pension means test (see `agePension.ts`) and by the residential aged-care means test
 * (server-side, `agedCareCostsEngine.ts`) — which is exactly why the assessment is separate from
 * either: the two tests disagree about thresholds and rates, but not about what a client owns.
 * Deeming, deprivation (gifting) and the reverse-mortgage proceeds are applied to these totals by the
 * caller, not here — see `calculateFinancials`'s sections 2 and 3.
 *
 * Extracted from `calculateFinancials`'s "--- 1. Assessable Assets & Income (for Pension) ---" block,
 * which still calls it — through the injectable {@link AssessableMeansAssessor} seam, so the projection
 * engine can hand in the assessor owned by its assessableMeansPlugin. See that plugin for why the code
 * lives here rather than in it.
 */

import { isDefinedBenefitPension, definedBenefitAssessableIncome, definedBenefitAssetValue, definedBenefitAnnualPayment, definedBenefitDeductibleAmount, definedBenefitAssetTestExempt, definedBenefitFundPreset, definedBenefitIndexationPct, pensionValuationFactor, DEFAULT_DB_INDEXATION_PCT } from '../../../../../shared/src/calculation/definedBenefitPension.ts';
import { PENSION_AGE, LIFE_EXPECTANCY_FACTORS_ABP, OTHER_INCOME_TYPES, FUNERAL_BOND_LIMIT, boardIncomeShare } from '../../../../../shared/src/calculation/constants/ratesAndThresholds.ts';
import { assessableHomeLandOf } from '../../../../../shared/src/calculation/homeLandRules.ts';
import { shouldSkipPersonalAssetProcessing } from '../../../../../shared/src/calculation/structureAttribution.ts';
import { annuityAssessableAssetOf, annuityAssessableIncomeOf, annuityTermsOf } from '../../../../../shared/src/calculation/annuityStrategies.ts';
import type { FactFind } from '../../../../../shared/src/calculation/factFindTypes.ts';
import type { StrategyOptions } from '../../../../../shared/src/calculation/strategyOptionsTypes.ts';

/**
 * The working behind one pre-2015 account-based pension's assessable income, for the Age Pension
 * breakdown's "Pre-2015 ABP (deductible amount)" section. Reported detail only — the assessable amount
 * itself is already inside {@link AssessableMeansAssessment.assessableIncomeFromPensions}.
 */
/** One defined benefit pension's treatment, for the Age Pension breakdown (definedBenefitPension.ts). */
export interface DefinedBenefitDetail {
    description: string;
    annualPayment: number;
    /** The fund's deductible amount as entered, and what the 10% cap let count. */
    deductibleEntered: number;
    deductibleApplied: number;
    assessableIncome: number;
    assetTestExempt: boolean;
    /** Annual payment × the pension valuation factor where counted; 0 where exempt. */
    assetValue: number;
    valuationFactor: number | null;
}

export interface Pre2015AbpDetail {
    description: string;
    /** The owner's life expectancy factor at commencement — what the purchase price is spread over. */
    relevantNumber: number;
    /** The tax-free-by-another-name slice of each year's payment: (purchase price − commutations) ÷ relevant number. */
    deductibleAmount: number;
    /** Annual payment less {@link deductibleAmount}, floored at 0. */
    assessableAmount: number;
    /** Lump sums taken out of the pension (e.g. a RAD drawdown), which reduce the deductible amount. */
    commutations: number;
}

/**
 * The already-resolved position the walk is run against. Everything here is state `calculateFinancials`
 * has settled before section 1 — notably the homeowner/retirement-village determinations, which depend
 * on the strategy overrides, and `structureAttribution`, whose trust/company apportionment is merged
 * into these totals rather than recomputed.
 */
export interface AssessableMeansInput {
    /** Read for `income.otherIncomes`, `assets`, `home` and the retirement-village entry contribution. */
    factFind: FactFind;
    /** Read for the what-if overrides that change the assessment: `rentHome`/`rentAmount`/`rentExpenses`, `downsizeHome`, `formerPrincipalHomeAssessable` and `radFunding`. */
    strategyOptions: StrategyOptions;
    isPartnered: boolean;
    /** Ages decide two things here: super below pension age is exempt, and an ABP's age at commencement. */
    clientAge: number | null;
    partnerAge: number | null;
    /** Residency AFTER any strategy override — whether anyone still lives in the family home. */
    clientResidency: string | undefined;
    partnerResidency: string | undefined;
    /**
     * How long the family home has been rented out under the rent-the-home strategy. Centrelink exempts
     * a rented former home from the assets test for two years; from the third it counts.
     */
    yearsSinceRented: number;
    /** Retirement-village resident (client or partner) — their entry contribution is assessed in place of a home. */
    rvResident: boolean;
    /**
     * Someone still HOLDS a village interest, whether or not the scenario has moved them into care.
     * Absent reads as `rvResident` (the pre-7 Oct 2026 behaviour, where a resident modelled into
     * care had the contribution drop out of the assets test altogether).
     */
    rvInterestHeld?: boolean;
    /** Entry contribution above the Extra Allowable Amount, which makes them a HOMEOWNER and the EC exempt instead. */
    rvIsHomeowner: boolean;
    rvEntryContribution: number;
    /** A granny flat interest at or below the Extra Allowable Amount: what was paid, counted as an asset (not deemed). Zero where there is none or the resident is a homeowner. */
    grannyFlatCountedInterest?: number;
    /** The trust/company apportionment, or null when the client has no structures. Merged in, not recomputed. */
    structureAttribution: any;
}

/**
 * What the client owns and earns, as the means tests see it. Every income figure is annual, and every
 * partner figure is the partner's own share — a couple's totals are combined by the test, not here.
 */
export interface AssessableMeansAssessment {
    /** Total assessable assets, INCLUDING an assessable home (a rented former home, or one nobody lives in). */
    assessableAssets: number;
    /**
     * The same total with no home in it at all. Not simply `assessableAssets` minus the home value: the
     * home-shaped additions (the rented former home, a retained former principal home) are never added
     * to this one in the first place.
     */
    assessableAssetsNoHome: number;
    /** Financial assets — cash, term deposits, shares, ABPs, non-lifetime annuities — which the caller then deems. */
    financialAssetsForDeeming: number;
    assessableIncomeFromPensions: number;
    netRentalIncomeForPension: number;
    otherAssessableIncome: number;
    investmentPropertyRentalForPension: number;
    partnerAssessableIncomeFromPensions: number;
    partnerNetRentalIncomeForPension: number;
    partnerOtherAssessableIncome: number;
    partnerInvestmentPropertyRentalForPension: number;
    /** Breakdown detail for the Age Pension tooltip; empty unless the client holds a pre-2015 ABP. */
    pre2015ABPDetails: Pre2015AbpDetail[];
    /** Breakdown detail for each defined benefit pension; empty where there is none. */
    definedBenefitDetails: DefinedBenefitDetail[];
}

/**
 * Assesses what counts as an assessable asset and as assessable income. Injected into
 * `calculateFinancials` so a caller can own the rules — see {@link calculateAssessableMeans}, the
 * default, and the projection engine's assessableMeansPlugin.
 */
export type AssessableMeansAssessor = (input: AssessableMeansInput) => AssessableMeansAssessment;

/**
 * Walks a client's income streams and then their assets, attributing every assessable dollar to the
 * client, the partner, or half to each. One pass over the assets decides all of it — deeming
 * eligibility, the pension-age test on superannuation, the funeral-bond exemption as it is consumed,
 * and the per-category assessable value (an annuity's `assessableAsset` override where it has one, the
 * asset's value otherwise).
 */
export const calculateAssessableMeans: AssessableMeansAssessor = ({
    factFind,
    strategyOptions,
    isPartnered,
    clientAge,
    partnerAge,
    clientResidency,
    partnerResidency,
    yearsSinceRented,
    rvResident,
    rvInterestHeld,
    rvIsHomeowner,
    rvEntryContribution,
    grannyFlatCountedInterest = 0,
    structureAttribution,
}: AssessableMeansInput): AssessableMeansAssessment => {
    const { assets, income = {} } = factFind;

    let assessableAssets = 0;
    let assessableAssetsNoHome = 0;
    let financialAssetsForDeeming = 0;
    let assessableIncomeFromPensions = 0;
    let netRentalIncomeForPension = 0;
    let otherAssessableIncome = 0;

    let partnerAssessableIncomeFromPensions = 0;
    let partnerNetRentalIncomeForPension = 0;
    let partnerOtherAssessableIncome = 0;
    let investmentPropertyRentalForPension = 0;
    let partnerInvestmentPropertyRentalForPension = 0;
    const definedBenefitDetails: DefinedBenefitDetail[] = [];

    // Process 'Other Incomes'
    (income.otherIncomes || []).forEach((inc) => {
        const incomeType = OTHER_INCOME_TYPES.find((t) => t.id === inc.type);

        // A DEFINED BENEFIT PENSION (SS Guide 4.9.3.30, 4.9.2.20, 4.9.5.60; definedBenefitPension.ts):
        // income = the payment less its deductible amount, the deductible capped at 10% of the payment;
        // an asset only where the pension product postdates 20 Sep 1998 (then payment × the valuation
        // factor for the owner's age), never deemed.
        if (isDefinedBenefitPension(inc)) {
            const assessable = definedBenefitAssessableIncome(inc);
            const ownerAge = inc.ownership === 'partner' && isPartnered ? partnerAge : clientAge;
            const assetValue = definedBenefitAssetValue(inc, ownerAge, DEFAULT_DB_INDEXATION_PCT);
            if (inc.ownership === 'client') {
                otherAssessableIncome += assessable;
            } else if (inc.ownership === 'partner' && isPartnered) {
                partnerOtherAssessableIncome += assessable;
            } else {
                otherAssessableIncome += assessable / 2;
                if (isPartnered) partnerOtherAssessableIncome += assessable / 2;
            }
            if (assetValue > 0) {
                assessableAssets += assetValue;
                assessableAssetsNoHome += assetValue;
            }
            const payment = definedBenefitAnnualPayment(inc);
            const entered = definedBenefitDeductibleAmount(inc);
            const exempt = definedBenefitAssetTestExempt(inc);
            definedBenefitDetails.push({
                description: inc.description || definedBenefitFundPreset(inc.dbFund).name,
                annualPayment: payment,
                deductibleEntered: entered,
                deductibleApplied: payment - assessable,
                assessableIncome: assessable,
                assetTestExempt: exempt,
                assetValue,
                valuationFactor: exempt || ownerAge == null ? null : pensionValuationFactor(ownerAge, definedBenefitIndexationPct(inc, DEFAULT_DB_INDEXATION_PCT)),
            });
            return;
        }

        let isAssessable = incomeType?.assessable;
        if (incomeType?.isCustom) {
            isAssessable = inc.isAssessable;
        }
        if (isAssessable) {
            // Boarders and lodgers: only part of the payment is income (boardIncomeShare).
            const amount = (inc.amount || 0) * (inc.type === 'boarders' ? boardIncomeShare(inc.boardType) : 1);
            if (inc.ownership === 'client') {
                otherAssessableIncome += amount;
            } else if (inc.ownership === 'partner' && isPartnered) {
                partnerOtherAssessableIncome += amount;
            } else {
                otherAssessableIncome += amount / 2;
                if (isPartnered) partnerOtherAssessableIncome += amount / 2;
            }
        }
    });

    if (strategyOptions.rentHome) {
        const annualRent = (strategyOptions.rentAmount || 0) * 52;
        const annualExpenses = strategyOptions.rentExpenses || 0;
        const netRental = annualRent - annualExpenses;

        if (yearsSinceRented >= 2) {
            const homeValue = factFind.home?.value || 0;
            assessableAssets += homeValue;
        }

        const homeOwnership = factFind.home?.ownership || 'joint';
        if (homeOwnership === 'client') {
            netRentalIncomeForPension += netRental;
        } else if (homeOwnership === 'partner') {
            if (isPartnered) {
                partnerNetRentalIncomeForPension += netRental;
            } else {
                netRentalIncomeForPension += netRental;
            }
        } else {
            netRentalIncomeForPension += netRental / 2;
            if (isPartnered) {
                partnerNetRentalIncomeForPension += netRental / 2;
            }
        }
    }

    // The family home is exempt from the assets test ONLY while it is someone's principal residence.
    const someoneLivesInFamilyHome = clientResidency === 'own_home' || (isPartnered && partnerResidency === 'own_home');
    const inAgedCare = clientResidency === 'aged_care' || (isPartnered && partnerResidency === 'aged_care');
    const retainedFormerHomeAssessable = !someoneLivesInFamilyHome && !inAgedCare && !strategyOptions.rentHome && !strategyOptions.downsizeHome;
    if (strategyOptions.formerPrincipalHomeAssessable || retainedFormerHomeAssessable) {
        const homeValue = factFind.home?.value || 0;
        if (homeValue > 0) {
            assessableAssets += homeValue;
        }
    }

    /*
     * LAND THE HOME EXEMPTION DOES NOT COVER — land on a separate title, and land beyond 2 hectares
     * where the extended land use test fails. See shared/src/calculation/homeLandRules.ts for the
     * rule and its sources.
     *
     * It goes into `assessableAssetsNoHome` as well as `assessableAssets`, and that placement is the
     * point: residential aged care counts the HOME only up to the capped amount, then adds
     * `assessableAssetsNoHome` on top at full value. Land outside the exemption is not the home, so
     * it must not inherit the cap.
     *
     * Skipped entirely when the whole home value has already been assessed above — a home rented for
     * two years or more, or a former home retained — because these values are a PORTION of that same
     * home value, and counting them again would assess the land twice.
     */
    const homeAlreadyAssessedInFull =
        (strategyOptions.rentHome && yearsSinceRented >= 2)
        || !!strategyOptions.formerPrincipalHomeAssessable
        || retainedFormerHomeAssessable;
    if (!homeAlreadyAssessedInFull) {
        const nonExemptLand = assessableHomeLandOf(factFind).total;
        if (nonExemptLand > 0) {
            assessableAssets += nonExemptLand;
            assessableAssetsNoHome += nonExemptLand;
        }
    }

    // Funeral bonds are exempt up to the limit PER PERSON, spent in the order the bonds are found.
    const funeralBondExemptLimit = isPartnered ? FUNERAL_BOND_LIMIT * 2 : FUNERAL_BOND_LIMIT;
    let funeralBondExemptUsed = 0;
    const pre2015ABPDetails: Pre2015AbpDetail[] = [];

    Object.entries(assets || {}).forEach(([type, assetList]) => {
        (assetList || []).forEach((asset) => {
            if (shouldSkipPersonalAssetProcessing(asset.ownership)) return;

            // Coerced on purpose: the API route guarantees numeric leaves (factFindNumericSchema), but a
            // fact-find built in a test with a text value would otherwise be CONCATENATED into the sum
            // ('0' + 1000 → '01000') and every figure downstream of it silently ten times too large.
            const value = Number(asset.value) || 0;
            /*
             * ANNUITIES, read one way (annuityStrategies.ts, F.7 — 17 Sep 2026). A lifetime annuity
             * counts at 60%/30% of its purchase price; a fixed term at its reducing value. A SHORT
             * term (five years or less) is a financial investment and is deemed below; a long term
             * and a lifetime annuity have their payments counted as income instead (further down).
             */
            const annuityTerms = type === 'annuities' ? annuityTermsOf(asset) : null;
            const annuityOwnerAge = asset.ownership === 'partner' ? partnerAge : clientAge;
            const assessableValue = annuityTerms ? annuityAssessableAssetOf(asset, annuityOwnerAge) : value;

            const isPre2015ABP = type === 'accountBasedPensions' && asset.commencementDateOption === 'pre-2015';
            if (
                !isPre2015ABP &&
                (['bankAccounts', 'termDeposits', 'shares', 'investmentBonds', 'accountBasedPensions', 'loansToOthers'].includes(type) || (annuityTerms != null && annuityTerms.kind === 'shortTerm'))
            ) {
                if (asset.ownership === 'client') {
                    financialAssetsForDeeming += value;
                } else if (asset.ownership === 'partner' && isPartnered) {
                    financialAssetsForDeeming += value;
                } else {
                    if (isPartnered) {
                        financialAssetsForDeeming += value;
                    } else {
                        financialAssetsForDeeming += value / 2;
                    }
                }
            }

            if (type === 'superannuation') {
                if (asset.ownership === 'client' && clientAge && clientAge < PENSION_AGE) return;
                if (asset.ownership === 'partner' && partnerAge && partnerAge < PENSION_AGE) return;
            }
            if (type === 'home') {
                if (!strategyOptions.rentHome && !strategyOptions.downsizeHome) {
                    assessableAssets += value;
                }
            } else if (type === 'radPaid') {
                // RAD is exempt from Age Pension calculations
            } else if (type === 'prepaidFuneral') {
                // Exempt with no cap: a non-refundable contract with a funeral director (assetTypes.ts).
            } else if (type === 'funeralBonds') {
                const remainingExempt = funeralBondExemptLimit - funeralBondExemptUsed;
                const exemptPortion = Math.min(value, remainingExempt);
                const assessablePortion = Math.max(0, value - exemptPortion);
                funeralBondExemptUsed += exemptPortion;

                if (assessablePortion > 0) {
                    assessableAssets += assessablePortion;
                    assessableAssetsNoHome += assessablePortion;
                }
            } else if (type === 'investmentProperty') {
                const rent = asset.rent || 0;
                const expenses = asset.expenses || 0;
                const netRental = Math.max(0, rent - expenses);
                if (asset.ownership === 'client') {
                    investmentPropertyRentalForPension += netRental;
                } else if (asset.ownership === 'partner' && isPartnered) {
                    partnerInvestmentPropertyRentalForPension += netRental;
                } else {
                    const half = netRental / 2;
                    investmentPropertyRentalForPension += half;
                    if (isPartnered) partnerInvestmentPropertyRentalForPension += half;
                }
                assessableAssets += assessableValue;
                assessableAssetsNoHome += assessableValue;
            } else {
                assessableAssets += assessableValue;
                assessableAssetsNoHome += assessableValue;
            }
            // Pre-1 Jan 2015 ABP: assessable income = annual payment minus deductible amount.
            if (isPre2015ABP) {
                const purchasePrice = asset.purchasePrice || 0;
                let commutations = 0;
                if (strategyOptions.radFunding && type === 'accountBasedPensions') {
                    const radKey = `${type}-${asset.id}`;
                    commutations = Math.max(0, parseFloat(String(strategyOptions.radFunding[radKey])) || 0);
                }
                if (commutations === 0 && asset.__originalValue != null) {
                    const orig = asset.__originalValue || 0;
                    const curr = asset.value || 0;
                    commutations = Math.max(0, orig - curr);
                }
                const annualPayment = asset.income || 0;
                let ownerAge: number | null;
                if (asset.ownership === 'client') ownerAge = clientAge;
                else if (asset.ownership === 'partner' && isPartnered) ownerAge = partnerAge;
                else if (isPartnered) ownerAge = clientAge != null && partnerAge != null ? Math.min(clientAge, partnerAge) : clientAge || partnerAge;
                else ownerAge = clientAge;
                let ageAtCommencement = ownerAge != null ? ownerAge : 65;
                if (asset.commencementDate && !asset.commencementDateUnsure) {
                    const commDate = new Date(asset.commencementDate);
                    const now = new Date();
                    const yearsSince = (now.getTime() - commDate.getTime()) / (365.25 * 24 * 60 * 60 * 1000);
                    ageAtCommencement = Math.round(ageAtCommencement - yearsSince);
                }
                const ageClamped = Math.min(95, Math.max(65, ageAtCommencement));
                const relevantNumber = LIFE_EXPECTANCY_FACTORS_ABP[ageClamped] != null ? LIFE_EXPECTANCY_FACTORS_ABP[ageClamped]! : LIFE_EXPECTANCY_FACTORS_ABP[95]!;
                const deductibleAmount = relevantNumber > 0 ? Math.max(0, purchasePrice - commutations) / relevantNumber : 0;
                const assessableFromABP = Math.max(0, annualPayment - deductibleAmount);
                pre2015ABPDetails.push({
                    description: (asset.description || 'ABP').trim() || 'ABP',
                    relevantNumber,
                    deductibleAmount,
                    assessableAmount: assessableFromABP,
                    commutations,
                });
                if (asset.ownership === 'client') {
                    assessableIncomeFromPensions += assessableFromABP;
                } else if (asset.ownership === 'partner' && isPartnered) {
                    partnerAssessableIncomeFromPensions += assessableFromABP;
                } else {
                    const half = assessableFromABP / 2;
                    assessableIncomeFromPensions += half;
                    if (isPartnered) partnerAssessableIncomeFromPensions += half;
                }
            }
            /*
             * ANNUITY PAYMENTS IN THE INCOME TEST. Until 17 Sep 2026 no annuity's payments reached
             * this test at all — the asset was counted and the income forgotten, which flattered every
             * annuity strategy by the income test's 50-cent taper on 60% of the payments. Lifetime:
             * 60% of the payments (nil while deferred). Long-term fixed: payment less the deductible
             * amount. Short-term fixed: nothing here — it was deemed above.
             */
            if (annuityTerms) {
                const assessableFromAnnuity = annuityAssessableIncomeOf(asset);
                if (assessableFromAnnuity > 0) {
                    if (asset.ownership === 'client') {
                        assessableIncomeFromPensions += assessableFromAnnuity;
                    } else if (asset.ownership === 'partner' && isPartnered) {
                        partnerAssessableIncomeFromPensions += assessableFromAnnuity;
                    } else {
                        const half = assessableFromAnnuity / 2;
                        assessableIncomeFromPensions += half;
                        if (isPartnered) partnerAssessableIncomeFromPensions += half;
                    }
                }
            }
        });
    });

    if (structureAttribution) {
        assessableAssets += structureAttribution.assessableAssets;
        assessableAssetsNoHome += structureAttribution.assessableAssetsNoHome;
        financialAssetsForDeeming += structureAttribution.financialAssetsForDeeming;
        investmentPropertyRentalForPension += structureAttribution.investmentPropertyRentalForPension;
        partnerInvestmentPropertyRentalForPension += structureAttribution.partnerInvestmentPropertyRentalForPension;
        assessableIncomeFromPensions += structureAttribution.assessableIncomeFromPensions;
        partnerAssessableIncomeFromPensions += structureAttribution.partnerAssessableIncomeFromPensions;
        otherAssessableIncome += structureAttribution.clientDistributionMeansTestIncome || 0;
        partnerOtherAssessableIncome += structureAttribution.partnerDistributionMeansTestIncome || 0;
    }

    // A retirement-village entry contribution at or below the Extra Allowable Amount is assessed as an
    // asset in its own right (and NOT deemed); above it the resident is a homeowner and it is exempt.
    if ((rvInterestHeld ?? rvResident) && !rvIsHomeowner && rvEntryContribution > 0) {
        assessableAssets += rvEntryContribution;
        assessableAssetsNoHome += rvEntryContribution;
    }
    // The granny flat twin of the rule above (Social Security Guide 4.6.4, special residences).
    if (grannyFlatCountedInterest > 0) {
        assessableAssets += grannyFlatCountedInterest;
        assessableAssetsNoHome += grannyFlatCountedInterest;
    }

    return {
        assessableAssets,
        assessableAssetsNoHome,
        financialAssetsForDeeming,
        assessableIncomeFromPensions,
        netRentalIncomeForPension,
        otherAssessableIncome,
        investmentPropertyRentalForPension,
        partnerAssessableIncomeFromPensions,
        partnerNetRentalIncomeForPension,
        partnerOtherAssessableIncome,
        partnerInvestmentPropertyRentalForPension,
        pre2015ABPDetails,
        definedBenefitDetails,
    };
};
